import PlannerStartTime, { formatPlannerTime } from './PlannerStartTime';
import { OfficeInstructions, InstructionEditor } from "@/components/ScheduleInstructions";
// Day Board — single-day dispatch view inside Crew Planner. Functional parity
// with the retired /app/dispatch board: drag workers onto jobs, day-off zone,
// per-worker start times + tasks, per-job vehicle. Same db.assignments rows.
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { nzTodayIso, shiftIsoDay } from "@/lib/dates";
import { ConflictList } from "@/components/PlannerAllocation";
import api from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { DispatchListView } from "@/components/DispatchListView";
import { DispatchCrewTasks } from "@/components/DispatchTaskPicker";
import WeatherStrip from "@/components/WeatherStrip";
import WeatherChip from "@/components/WeatherChip";
import {
  Calendar, Users as UsersIcon, Truck, MapPin, Sun, ChevronLeft, ChevronRight,
  X, LayoutGrid, List as ListIcon, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export function isoDay(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const fmtPretty = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" });

export default function DayBoard({ date, setDate }) {
  const tomorrowIso = shiftIsoDay(nzTodayIso(), 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [instructionJob,setInstructionJob]=useState(null);
  const [dragId, setDragId] = useState(null);
  const [showFullForecast, setShowFullForecast] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("fl_dispatch_view") || "board"; } catch { return "board"; }
  });
  const setView = (v) => {
    setViewMode(v);
    try { localStorage.setItem("fl_dispatch_view", v); } catch { /* noop */ }
  };

  const requestRef = useRef(null);
  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    try {
      const { data } = await api.get("/dispatch", { params: { date }, signal: controller.signal });
      if (!controller.signal.aborted) setData(data);
    } catch (e) {
      if (!controller.signal.aborted) toast.error(e?.response?.data?.detail || "Failed to load day board");
    }
  }, [date]);
  useEffect(() => { load(); return () => requestRef.current?.abort(); }, [load, viewMode]);

  const orgWeatherLoc = useMemo(() => {
    const firstGeo = (data?.jobs || []).find((j) => j.site_lat && j.site_lng);
    if (firstGeo) return { lat: firstGeo.site_lat, lng: firstGeo.site_lng, address: firstGeo.site_address };
    return { lat: -36.85, lng: 174.76, address: "Auckland (fallback)" };
  }, [data?.jobs]);

  const [orgForecast, setOrgForecast] = useState(null);
  useEffect(() => {
    let mounted = true;
    if (!orgWeatherLoc.lat || !orgWeatherLoc.lng) return;
    api.get("/weather/forecast", { params: { lat: orgWeatherLoc.lat, lng: orgWeatherLoc.lng } })
      .then((r) => mounted && setOrgForecast(r.data))
      .catch(() => mounted && setOrgForecast(null));
    return () => { mounted = false; };
  }, [orgWeatherLoc.lat, orgWeatherLoc.lng]);

  const dayForecast = useMemo(
    () => orgForecast?.days?.find((d) => d.date === date) || null,
    [orgForecast, date]);

  const userById = useMemo(() => Object.fromEntries((data?.users || []).map((u) => [u.id, u])), [data]);
  const assignmentsByUser = useMemo(() => {
    const m = {};
    for (const a of (data?.assignments || [])) (m[a.user_id] ||= []).push(a);
    return m;
  }, [data]);
  const vehAssignmentByJob = useMemo(
    () => Object.fromEntries((data?.vehicle_assignments || []).map((v) => [v.job_id, v])), [data]);
  const unassignedUsers = useMemo(
    () => (data ? data.users.filter((u) => (assignmentsByUser[u.id] || []).length === 0) : []),
    [data, assignmentsByUser]);
  const noWorkUsers = useMemo(
    () => (data ? data.users.filter((u) => (assignmentsByUser[u.id] || []).some((a) => a.job_id === null)) : []),
    [data, assignmentsByUser]);
  const crewByJob = useMemo(() => {
    const m = {};
    for (const a of (data?.assignments || [])) {
      if (!a.job_id) continue;
      (m[a.job_id] ||= []).push({ userId: a.user_id, assignmentId: a.id, taskIds: a.task_ids || [], startTime: a.start_time || "" });
    }
    return m;
  }, [data]);

  const assign = async (userId, jobId) => {
    setBusy(true);
    try {
      const result = await api.post("/crew-planner/assign", { user_ids: [userId], dates: [date], job_id: jobId });
      (result.data.warnings || []).slice(0, 5).forEach(w=>toast.warning(w));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };
  const removeAssignment = async (assignmentId) => {
    setBusy(true);
    try { await api.delete(`/dispatch/assignment/${assignmentId}`); await load(); }
    catch { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  const setStartTime = async (assignmentId, st) => {
    setData((d) => d ? { ...d, assignments: d.assignments.map((a) => a.id === assignmentId ? { ...a, start_time: st || null } : a) } : d);
    try { await api.patch(`/assignments/${assignmentId}`, { start_time: st || null }); }
    catch { toast.error("Couldn't save start time"); load(); }
  };
  const clearUserDay = async (userId) => {
    setBusy(true);
    try { await api.delete("/dispatch/assignments", { params: { user_id: userId, date } }); await load(); }
    catch { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  const setVehicleForJob = async (jobId, vehicleId) => {
    setBusy(true);
    try { const r = await api.put("/dispatch/vehicle", { date, job_id: jobId, vehicle_id: vehicleId || null }); (r.data.warnings || []).forEach(w=>toast.warning(w)); await load(); }
    catch { toast.error("Vehicle update failed"); }
    finally { setBusy(false); }
  };

  const onDragStart = (e, userId) => {
    setDragId(userId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", userId);
  };
  const onDragEnd = () => setDragId(null);
  const dropOnJob = (jobId) => (e) => {
    e.preventDefault();
    const uid = e.dataTransfer.getData("text/plain") || dragId;
    if (uid) assign(uid, jobId);
  };
  const dropOnUnassigned = (e) => {
    e.preventDefault();
    const uid = e.dataTransfer.getData("text/plain") || dragId;
    if (uid) clearUserDay(uid);
  };
  const dropOnDayOff = (e) => {
    e.preventDefault();
    const uid = e.dataTransfer.getData("text/plain") || dragId;
    if (uid) api.put("/assignments", { user_id: uid, date, job_id: null }).then(load);
  };

  const shiftDate = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(isoDay(d));
  };

  if (!data) return <div className="p-6"><div className="animate-pulse text-zinc-400">Loading day board…</div></div>;

  return (
    <div className="bg-white dark:bg-[#0B1520] p-2 text-zinc-900 dark:text-slate-100" data-testid="day-board">
      {instructionJob&&<InstructionEditor job={instructionJob} date={date} crew={data.crew_instructions?.find(n=>n.job_id===instructionJob.id)} onClose={()=>setInstructionJob(null)} onSaved={load}/>}
      {!!data.conflicts?.length && <details className="mb-3 border border-amber-400 bg-white dark:bg-[#101e2e] p-3"><summary className="cursor-pointer text-sm font-bold">⚠ {data.conflicts.length} schedule / leave / Plant conflicts</summary><ConflictList conflicts={data.conflicts}/></details>}
      {!!data.leave?.length && <details className="mb-3 text-xs"><summary>Approved leave · {data.leave.length}</summary>{data.leave.map((l,i)=><p key={i}>{userById[l.user_id]?.name || 'Worker'} · {l.leave_type?.replaceAll('_',' ')} · {l.start_date}–{l.end_date}</p>)}</details>}
      {/* Compact weather bar */}
      <div className="mb-4" data-testid="dayboard-weather-bar">
        <div className="hard-card px-3 py-2 flex items-center gap-3 flex-wrap">
          <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{fmtPretty(date)}</span>
          {dayForecast ? (
            <>
              <WeatherChip day={dayForecast} />
              <span className="text-[11px] font-mono text-zinc-500 hidden sm:inline">
                {Math.round(dayForecast.temp_max ?? 0)}° · {(dayForecast.rain_mm || 0).toFixed(1)}mm · {(dayForecast.wind_kmh || 0).toFixed(0)}km/h · {orgWeatherLoc.address || "regional"}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-zinc-400 italic">No forecast for this date</span>
          )}
          <button
            onClick={() => setShowFullForecast((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]"
            data-testid="dayboard-full-forecast-toggle">
            {showFullForecast ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showFullForecast ? "Hide forecast" : "Full forecast"}
          </button>
        </div>
        {showFullForecast && (
          <div className="mt-2">
            <WeatherStrip
              title={`Forecast · ${orgWeatherLoc.address || "regional"}`}
              lat={orgWeatherLoc.lat} lng={orgWeatherLoc.lng}
              highlightDate={date} onDayClick={(d) => setDate(d)} />
          </div>
        )}
      </div>

      {/* Date selector + view toggle */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="dayboard-date-bar">
        <Button variant="outline" size="icon" className="rounded-sm border-zinc-400 h-9 w-9" onClick={() => shiftDate(-1)} data-testid="dayboard-prev-day">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2 px-3 h-9 border-2 border-zinc-900 bg-white dark:bg-[#101620] dark:border-white/20">
          <Calendar className="w-4 h-4 text-[#FF5F15]" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="h-7 w-36 border-0 px-1 focus-visible:ring-0 font-mono text-sm"
            data-testid="dayboard-date-picker" />
        </div>
        <Button variant="outline" size="icon" className="rounded-sm border-zinc-400 h-9 w-9" onClick={() => shiftDate(1)} data-testid="dayboard-next-day">
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" className="rounded-sm font-bold uppercase tracking-wider" onClick={() => setDate(nzTodayIso())} data-testid="dayboard-jump-today">
          Today
        </Button>
        <Button variant="outline" size="sm" className="rounded-sm font-bold uppercase tracking-wider" onClick={() => setDate(tomorrowIso)} data-testid="dayboard-jump-tomorrow">
          Tomorrow
        </Button>
        <div className="ml-auto flex items-center border-2 border-zinc-900 dark:border-white/20" data-testid="dayboard-view-toggle">
          <button onClick={() => setView("board")}
            className={`px-3 h-9 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors ${viewMode === "board" ? "bg-[#FF5F15] text-white" : "bg-white dark:bg-[#101620] text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100"}`}
            data-testid="dayboard-view-board">
            <LayoutGrid className="w-3.5 h-3.5" /> Board
          </button>
          <button onClick={() => setView("list")}
            className={`px-3 h-9 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors border-l-2 border-zinc-900 dark:border-white/20 ${viewMode === "list" ? "bg-[#FF5F15] text-white" : "bg-white dark:bg-[#101620] text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100"}`}
            data-testid="dayboard-view-list">
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
        </div>
        {busy && <span className="text-xs font-mono text-zinc-500">Saving…</span>}
      </div>

      {viewMode === "list" && <DispatchListView onChanged={load} date={date} />}

      {viewMode === "board" && (
        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-3 space-y-4">
            <CrewPool title="Available / unassigned" subtitle="Drag onto a job →"
              users={unassignedUsers} onDragStart={onDragStart} onDragEnd={onDragEnd}
              onDragOver={(e) => e.preventDefault()} onDrop={dropOnUnassigned}
              testId="unassigned-pool" empty="Everyone is assigned 🎉" />
            <DayOffZone users={noWorkUsers} assignmentsByUser={assignmentsByUser}
              onDragOver={(e) => e.preventDefault()} onDrop={dropOnDayOff}
              onDragStart={onDragStart} onDragEnd={onDragEnd} removeAssignment={removeAssignment} />
          </div>
          <div className="lg:col-span-9">
            {data.jobs.length === 0 ? (
              <div className="hard-card p-10 text-center text-zinc-500" data-testid="dayboard-no-jobs">
                No active jobs. <Link to="/app/jobs" className="text-[#FF5F15] font-bold uppercase tracking-wider">Create a job →</Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.jobs.map((j) => (
                  <JobDispatchCard key={j.id} job={j} date={date}
                    crew={(crewByJob[j.id] || []).map(({ userId, assignmentId, taskIds, startTime }) => ({
                      user: userById[userId], assignmentId, taskIds, startTime,
                    })).filter((c) => c.user)}
                    crewInstruction={data.crew_instructions?.find(n=>n.job_id===j.id)} onInstruction={()=>setInstructionJob(j)} vehicleAssignment={vehAssignmentByJob[j.id]} fleet={data.fleet}
                    onDragStart={onDragStart} onDragEnd={onDragEnd}
                    onDragOver={(e) => e.preventDefault()} onDrop={dropOnJob(j.id)}
                    onRemoveAssignment={removeAssignment} onSetStartTime={setStartTime}
                    onSetVehicle={(vid) => setVehicleForJob(j.id, vid)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CrewPool({ title, subtitle, users, onDragStart, onDragEnd, onDragOver, onDrop, testId, empty }) {
  return (
    <div className="hard-card p-4 min-h-[140px]" onDragOver={onDragOver} onDrop={onDrop} data-testid={testId}>
      <div className="label-tag">{title}</div>
      {subtitle && <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">{subtitle}</div>}
      <div className="mt-3 flex flex-wrap gap-2">
        {users.length === 0 ? (
          <div className="text-xs text-zinc-400 italic py-2">{empty}</div>
        ) : (
          users.map((u) => <CrewChip key={u.id} user={u} onDragStart={onDragStart} onDragEnd={onDragEnd} />)
        )}
      </div>
    </div>
  );
}

function DayOffZone({ users, assignmentsByUser, onDragStart, onDragEnd, onDragOver, onDrop, removeAssignment }) {
  return (
    <div className="hard-card p-4 min-h-[100px] bg-zinc-50 dark:bg-[#101620]" onDragOver={onDragOver} onDrop={onDrop} data-testid="dayoff-zone">
      <div className="flex items-center gap-2">
        <Sun className="w-3.5 h-3.5 text-yellow-600" />
        <div className="label-tag">Day off · drop to mark</div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {users.length === 0 ? (
          <div className="text-xs text-zinc-400 italic py-1">No one on day off</div>
        ) : (
          users.map((u) => {
            const dayOff = (assignmentsByUser[u.id] || []).find((a) => a.job_id === null);
            return (
              <CrewChip key={u.id} user={u} onDragStart={onDragStart} onDragEnd={onDragEnd}
                onRemove={dayOff ? () => removeAssignment(dayOff.id) : undefined} variant="dayoff" />
            );
          })
        )}
      </div>
    </div>
  );
}

function CrewChip({ user, onDragStart, onDragEnd, onRemove, variant, startTime, onStartTimeChange }) {
  if (!user) return null;
  const initials = user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const bgColor = variant === "dayoff" ? "bg-yellow-50 border-yellow-300" : "bg-zinc-100 dark:bg-white/10 border-zinc-300 dark:border-white/15";
  const iconColor = user.role === "field" ? "bg-[#FF5F15]" : "bg-[#09090B]";
  return (
    <div draggable onDragStart={(e) => onDragStart(e, user.id)} onDragEnd={onDragEnd}
      className={`group inline-flex items-center gap-1.5 ${bgColor} border pl-1 pr-2 py-1 text-xs cursor-grab active:cursor-grabbing hover:border-[#FF5F15] transition-colors select-none`}
      data-testid={`crew-chip-${user.id}`}>
      <span className={`w-5 h-5 ${iconColor} text-white rounded-full flex items-center justify-center text-[11px] font-display font-black`}>
        {initials}
      </span>
      <span className="font-semibold">{user.name.split(" ")[0]}</span>
      {onStartTimeChange && (
        <details className="max-w-full cursor-auto" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} draggable={false}>
          <summary className="min-h-10 flex items-center cursor-pointer px-2 text-xs text-zinc-700 dark:text-slate-200" aria-label={`Start time for ${user.name}`}>
            {startTime ? formatPlannerTime(startTime) : 'All day / Unspecified'}<span aria-hidden="true" className="ml-1">▾</span>
          </summary>
          <div className="w-56 max-w-full p-2">
            <PlannerStartTime value={startTime || ''} onChange={onStartTimeChange} label={`Start time for ${user.name}`} testId={`crew-chip-start-${user.id}`} />
          </div>
        </details>
      )}
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="opacity-0 group-hover:opacity-100 ml-0.5 text-zinc-400 hover:text-red-600 transition-opacity"
          data-testid={`crew-chip-remove-${user.id}`} aria-label="Remove">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function JobWeatherBadge({ lat, lng, date }) {
  const [day, setDay] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let mounted = true;
    if (!lat || !lng) { setLoaded(true); return; }
    api.get("/weather/forecast", { params: { lat, lng } })
      .then((r) => { if (!mounted) return; setDay((r.data?.days || []).find((x) => x.date === date) || null); })
      .catch(() => mounted && setDay(null))
      .finally(() => mounted && setLoaded(true));
    return () => { mounted = false; };
  }, [lat, lng, date]);
  if (!loaded || !lat || !lng) return null;
  return <WeatherChip day={day} compact />;
}

function JobDispatchCard({ crewInstruction, onInstruction, job, date, crew, vehicleAssignment, fleet, onDragStart, onDragEnd, onDragOver, onDrop, onRemoveAssignment, onSetStartTime, onSetVehicle, onTasksChanged }) {
  const vehicle = vehicleAssignment ? fleet.find((f) => f.id === vehicleAssignment.vehicle_id) : null;
  return (
    <div className="hard-card p-4 flex flex-col" onDragOver={onDragOver} onDrop={onDrop} data-testid={`dispatch-job-${job.id}`}>
      <div>
        <div className="flex items-start gap-2">
          <div className="font-display font-bold uppercase tracking-tight text-base leading-snug truncate flex-1">
            {job.job_number && <span className="font-mono text-[11px] text-[#FF5F15] mr-1.5">{job.job_number}</span>}
            {job.title}
          </div>
          <JobWeatherBadge lat={job.site_lat} lng={job.site_lng} date={date} />
        </div>
        {job.client_name && <div className="text-[11px] text-zinc-500 font-mono mt-0.5 truncate">{job.client_name}</div>}
        {job.site_address && (
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-zinc-600 dark:text-zinc-400">
            <MapPin className="w-3 h-3 mt-0.5 text-zinc-400 shrink-0" />
            <span className="truncate">{job.site_address}</span>
          </div>
        )}
      </div>
      <div className="mt-3 border-2 border-dashed border-zinc-300 dark:border-white/15 min-h-[64px] p-2 flex flex-wrap gap-1.5 content-start hover:border-[#FF5F15] transition-colors" data-testid={`dispatch-job-crew-${job.id}`}>
        {crew.length === 0 ? (
          <div className="text-[11px] text-zinc-400 italic self-center mx-auto py-2">
            <UsersIcon className="w-4 h-4 inline mr-1" /> Drop crew here
          </div>
        ) : (
          crew.map(({ user: u, assignmentId, startTime }) => (
            <CrewChip key={assignmentId} user={u} onDragStart={onDragStart} onDragEnd={onDragEnd}
              onRemove={() => onRemoveAssignment(assignmentId)} startTime={startTime}
              onStartTimeChange={(st) => onSetStartTime(assignmentId, st)} />
          ))
        )}
      </div>
      <OfficeInstructions crew={crewInstruction}/><button className="text-xs underline mt-2" onClick={onInstruction}>{crewInstruction?.text ? "Edit crew instruction" : "+ Crew instruction"}</button>
      <DispatchCrewTasks job={job} crew={crew} onSaved={onTasksChanged} />
      <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10">
        <div className="flex items-center gap-2">
          <Truck className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <div className="label-tag !text-zinc-500">Plant / vehicle</div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          {vehicle?.photo_path ? (
            <div className="w-10 h-10 shrink-0 overflow-hidden border border-zinc-200 bg-zinc-100">
              <AuthImage path={vehicle.photo_path} alt={vehicle.rego} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-zinc-100 dark:bg-white/10 border border-zinc-200 dark:border-white/10">
              <Truck className="w-4 h-4 text-zinc-400" />
            </div>
          )}
          <Select value={vehicleAssignment?.vehicle_id || "__none__"}
            onValueChange={(v) => onSetVehicle(v === "__none__" ? null : v)}>
            <SelectTrigger className="h-9 rounded-sm border-zinc-300 text-xs flex-1" data-testid={`dispatch-vehicle-select-${job.id}`}>
              <SelectValue placeholder="Pick a vehicle…" />
            </SelectTrigger>
            <SelectContent className="rounded-sm max-h-72">
              <SelectItem value="__none__">— None —</SelectItem>
              {fleet.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.rego} · {v.make} {v.model}{v.status === "maintenance" ? " — MAINTENANCE" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
